import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Users, CreditCard, TrendingUp, Gift, Bell, Shield, BarChart3, Settings, Mail, Crown, Star, FileText, Save, Trophy, Palette, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function AdminDashboard() {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvoiceLimits, setShowInvoiceLimits] = useState(false);
  const [invoiceLimits, setInvoiceLimits] = useState({
    free: 10,
    standard: 50,
    premium: 500
  });
  const [savingLimits, setSavingLimits] = useState(false);

  useEffect(() => {
    if (!user?.admin_role) {
      navigate('/dashboard');
      return;
    }
    fetchStats();
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceLimits = async () => {
    try {
      const response = await api.get('/invoices/admin/limits');
      if (response.data.limits) {
        setInvoiceLimits({
          free: response.data.limits.free?.invoice_limit || 10,
          standard: response.data.limits.standard?.invoice_limit || 50,
          premium: response.data.limits.premium?.invoice_limit || 500
        });
      }
    } catch (error) {
      console.error('Error fetching invoice limits:', error);
    }
  };

  const saveInvoiceLimits = async () => {
    setSavingLimits(true);
    try {
      await Promise.all([
        api.put(`/invoices/admin/limits/free?limit=${invoiceLimits.free}`),
        api.put(`/invoices/admin/limits/standard?limit=${invoiceLimits.standard}`),
        api.put(`/invoices/admin/limits/premium?limit=${invoiceLimits.premium}`)
      ]);
      toast.success(language === 'fr' ? 'Limites enregistrées !' : language === 'pt' ? 'Limites guardados!' : 'Limits saved!');
      setShowInvoiceLimits(false);
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de la sauvegarde' : language === 'pt' ? 'Erro ao guardar' : 'Error saving');
    } finally {
      setSavingLimits(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const statCards = [
    { title: 'Total Utilisateurs', value: stats?.total_users || 0, icon: Users, color: 'text-blue-500' },
    { title: 'Premium', value: stats?.premium_users || 0, icon: Crown, color: 'text-yellow-500' },
    { title: 'Standard', value: stats?.standard_users || 0, icon: Star, color: 'text-blue-500' },
    { title: 'Gratuit', value: stats?.free_users || 0, icon: Users, color: 'text-gray-500' },
    { title: 'Actifs (7j)', value: stats?.active_users_7d || 0, icon: BarChart3, color: 'text-orange-500' },
    { title: 'Conversion', value: `${stats?.conversion_rate || 0}%`, icon: TrendingUp, color: 'text-green-500' },
  ];

  const menuItems = [
    { title: 'Gestion Utilisateurs', icon: Users, path: '/admin/users', description: 'Voir, modifier, désactiver des comptes' },
    { title: 'Invitations', icon: Mail, path: '/admin/invitations', description: 'Inviter de nouveaux utilisateurs' },
    { title: 'Abonnements', icon: CreditCard, path: '/admin/subscriptions', description: 'Configurer les limites et prix', show: user?.admin_role === 'super_admin' },
    { title: 'Limites Factures', icon: FileText, onClick: () => { fetchInvoiceLimits(); setShowInvoiceLimits(true); }, description: 'Configurer les limites de stockage', show: user?.admin_role === 'super_admin' },
    { title: 'Défis du jour', icon: Trophy, path: '/admin/challenges', description: 'Gérer les défis quotidiens' },
    { title: 'Catégories & Emojis', icon: Palette, path: '/admin/categories', description: 'Gérer les emojis des catégories système' },
    { title: 'Prompts IA Simulateur', icon: Sparkles, path: '/admin/ai-prompts', description: 'Gérer les prompts IA du simulateur (global, par utilisateur, par type de budget)', show: user?.admin_role === 'super_admin' },
    { title: 'Codes Promo', icon: Gift, path: '/admin/promos', description: 'Créer et gérer les codes promo' },
    { title: 'Notifications', icon: Bell, path: '/admin/notifications', description: 'Envoyer des notifications' },
    { title: 'Administrateurs', icon: Shield, path: '/admin/admins', description: 'Gérer les rôles admin', show: user?.admin_role === 'super_admin' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-xl font-bold">Admin SpenSpot</h1>
              <p className="text-sm text-muted-foreground">{user?.admin_role}</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Retour à l'app
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {statCards.map((stat, index) => (
            <Card key={index}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  <span className="text-xs text-muted-foreground">{stat.title}</span>
                </div>
                <p className="text-2xl font-bold">{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Menu Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {menuItems.filter(item => item.show !== false).map((item, index) => (
            <Card 
              key={index} 
              className="cursor-pointer hover:border-primary transition-colors"
              onClick={() => item.onClick ? item.onClick() : navigate(item.path)}
            >
              <CardContent className="p-6">
                <item.icon className="w-10 h-10 text-primary mb-4" />
                <h3 className="font-semibold mb-1">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>

      {/* Invoice Limits Modal */}
      <Dialog open={showInvoiceLimits} onOpenChange={setShowInvoiceLimits}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {language === 'fr' ? 'Limites de stockage factures' : language === 'pt' ? 'Limites de armazenamento de faturas' : 'Invoice Storage Limits'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Free - {language === 'fr' ? 'Limite de factures' : language === 'pt' ? 'Limite de faturas' : 'Invoice limit'}
              </label>
              <Input
                type="number"
                value={invoiceLimits.free}
                onChange={(e) => setInvoiceLimits({ ...invoiceLimits, free: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Standard - {language === 'fr' ? 'Limite de factures' : language === 'pt' ? 'Limite de faturas' : 'Invoice limit'}
              </label>
              <Input
                type="number"
                value={invoiceLimits.standard}
                onChange={(e) => setInvoiceLimits({ ...invoiceLimits, standard: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Premium - {language === 'fr' ? 'Limite de factures' : language === 'pt' ? 'Limite de faturas' : 'Invoice limit'}
              </label>
              <Input
                type="number"
                value={invoiceLimits.premium}
                onChange={(e) => setInvoiceLimits({ ...invoiceLimits, premium: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceLimits(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={saveInvoiceLimits} disabled={savingLimits}>
              <Save className="w-4 h-4 mr-2" />
              {savingLimits ? t('loading') : t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
